import { BufferGeometry } from "three";
import { defaultDoorWidth } from "shared/utils/doorSceneConstants";

export interface GeometryBounds2D {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface OpeningEdgesX {
  left: number;
  right: number;
}

const geometryBoundsCache = new WeakMap<BufferGeometry, GeometryBounds2D>();

export const getGeometryBounds2D = (
  geometry: BufferGeometry,
): GeometryBounds2D => {
  const cachedBounds = geometryBoundsCache.get(geometry);

  if (cachedBounds) {
    return cachedBounds;
  }

  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }

  const boundingBox = geometry.boundingBox;

  if (!boundingBox) {
    const emptyBounds = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    geometryBoundsCache.set(geometry, emptyBounds);
    return emptyBounds;
  }

  const bounds = {
    minX: boundingBox.min.x,
    maxX: boundingBox.max.x,
    minY: boundingBox.min.y,
    maxY: boundingBox.max.y,
  };

  geometryBoundsCache.set(geometry, bounds);

  return bounds;
};

export const getOpeningEdgesX = (
  glassBounds: Pick<GeometryBounds2D, "minX" | "maxX">,
  width: number,
): OpeningEdgesX => {
  const horizontalScale = width / defaultDoorWidth;

  return {
    left: glassBounds.minX * horizontalScale,
    right: glassBounds.maxX * horizontalScale,
  };
};

export const getOpeningAnchoredX = ({
  glassBounds,
  width,
  defaultX,
  reverse = false,
}: {
  glassBounds: Pick<GeometryBounds2D, "minX" | "maxX">;
  width: number;
  defaultX: number;
  reverse?: boolean;
}): number => {
  const defaultOpeningEdges = getOpeningEdgesX(glassBounds, defaultDoorWidth);
  const openingEdges = getOpeningEdgesX(glassBounds, width);
  const defaultInsetFromOpening = reverse
    ? defaultOpeningEdges.right - defaultX
    : defaultX - defaultOpeningEdges.left;

  return reverse
    ? openingEdges.right - defaultInsetFromOpening
    : openingEdges.left + defaultInsetFromOpening;
};
